import { nodeConfig } from '@scripts/mixins';
import { settings } from '@scripts/helpers/config';
import * as CONSTANTS from '@scripts/helpers/constants';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const NAMESPACE = 'alert';
const DEFAULT_SELECTOR = '[data-js-alert-wrapper]';

/**
 * @typedef {Object} AlertSettings
 * @property {Object} [localStorage] - Storage configuration.
 * @property {string} [localStorage.key] - LocalStorage key for dismissal state.
 */
const DEFAULT_SETTINGS = {
	localStorage: {
		key: `${settings.namespace}-${NAMESPACE}`
	}
};

/**
 * Dismissable alert banner.
 */
class Alert {
	/**
	 * @param {HTMLElement} node
	 * @param {AlertSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
	}

	get storage() {
		return window.localStorage;
	}

	get storageValue() {
		return this.storage.getItem(this.config.localStorage.key);
	}

	get ajaxURL() {
		return window?.AlertURL;
	}

	/** @returns {?HTMLElement} */
	get elAlert() {
		return this.elNode.querySelector('[data-js-alert]');
	}

	get time() {
		return this.elAlert?.dataset.time || '';
	}

	/** @returns {?HTMLElement} */
	get elCloseButton() {
		return this.elAlert?.querySelector('[data-js-alert-close-button]');
	}

	init() {
		if (this.ajaxURL) {
			this._fetchMarkup(() => {
				this._setup();
				this._bindUI();
			});

			return;
		}

		this._setup();
		this._bindUI();
	}

	open() {
		this.elAlert?.classList.add('alert--visible');
		this.elAlert?.removeAttribute('inert');
		ScrollTrigger.refresh();
	}

	close() {
		this.elAlert?.classList.remove('alert--visible');
		this.elAlert?.setAttribute('inert', '');
		ScrollTrigger.refresh();
	}

	_setup() {
		this.elAlert?.classList.toggle('alert--enabled', true);

		if (this.storageValue !== this.time) {
			this.open();

			return;
		}

		this.close();
	}

	_bindUI() {
		this._onCloseClick = this._onCloseClick.bind(this);

		this.elCloseButton?.addEventListener('click', this._onCloseClick);
	}

	async _fetchMarkup(cb) {
		const alertResponse = await fetch(this.ajaxURL);

		if (!alertResponse.ok) return;

		const alertHTML = await alertResponse.text();
		const parser = new DOMParser();
		const alertDoc = parser.parseFromString(alertHTML, 'text/html');
		const alertElement = alertDoc.querySelector('[data-js-alert]');

		if (!alertElement) return;

		this.elNode.innerHTML = alertElement.outerHTML;

		if (cb && typeof cb === 'function') {
			cb();
		}
	}

	_onCloseClick(event) {
		event.preventDefault();

		this.storage.setItem(this.config.localStorage.key, this.time);

		this.close();

		const nextFocusable = Array.from(
			document.querySelectorAll(CONSTANTS.FOCUSABLE_ELEMENTS_SELECTOR)
		).find((el) => !this.elNode.contains(el));

		if (nextFocusable) nextFocusable.focus();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const node = document.querySelector(selector);

	if (!node) return;

	const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
	const module = new Alert(node, config);

	module.init();

	return module;
}
